/** @jsx jsx */
import { jsx } from '@emotion/react';
import { ServiceIconShell } from '../ServiceIconShell';

import { Img } from '../../display/Img';

import { ServiceIconSize, ServiceIconConfig } from './constants';

type ServiceIconProps = {
  /**
   * Applies a custom alt attribute to the underlying Img component.
   */
  alt?: string;
  /**
   * Inform screen-readers that they should ignore the image. Image will still be visually displayed.
   */
  ariaHidden?: boolean;
  /**
   * Applies a shadow to the ServiceIcon. It is recommended to use `hasShadow`
   * at sizes 80px and larger.
   */
  hasShadow?: boolean;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be contained in a larger, bordered container.
   */
  hasBorder?: boolean;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
  /**
   * The name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   */
  serviceName: string;
  /**
   * A URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  src: string;
  /**
   * Determines the size of the shell and icon.
   *
   * _When the custom-properties.css stylesheet is not imported_, `size` determines the size of the shell (in pixels).
   *
   * _When the custom-properties.css stylesheet is imported_, `size` maps to the following shell sizes
   * - 20: 24x24
   * - 30: 24x24
   * - 40: 36x36
   * - 60: 48x48
   * - 80: 48x4
   * - 120: 48x48
   * - 150: 48x48
   */
  size: ServiceIconSize;
};

/**
 * Renders an icon representing a service (aka app, aka integration).
 */
export const ServiceIcon = ({
  alt,
  ariaHidden = false,
  hasBorder = true,
  hasShadow = false,
  hasWhiteBackground = false,
  serviceName,
  size,
  src,
}: ServiceIconProps) => {
  const { iconSize } = ServiceIconConfig[size];
  return (
    <ServiceIconShell
      hasBorder={hasBorder}
      hasShadow={hasShadow}
      hasWhiteBackground={hasWhiteBackground}
      size={size}
    >
      <Img
        alt={alt || `${serviceName} logo`}
        ariaHidden={ariaHidden}
        borderRadius={0}
        height={iconSize}
        width={iconSize}
        objectFit="contain"
        src={src}
      />
    </ServiceIconShell>
  );
};
